/**
 * A filter in a table column header: a small funnel that opens a popover. Four kinds:
 * - text: "contains" (`field_q`);
 * - choice: any of several values (the filter value is an array, which the list engine matches);
 * - range: a number between a minimum and a maximum (`field@gte`, `field@lte`);
 * - preset: one of a few named filters, e.g. "Overdue" or "Not touched in 30 days".
 * The values live in the list's URL filter, like every other list filter.
 */
import { ListFilter } from "lucide-react";
import { useListContext } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type ColumnFilterSpec =
  | { type: "text"; field: string }
  | {
      type: "choice";
      field: string;
      options: { value: string; label: string }[];
    }
  | { type: "range"; field: string }
  | {
      type: "preset";
      options: { id: string; label: string; filter: Record<string, unknown> }[];
    };

/** The list filter keys a column filter sets, so it can clear them. */
const columnFilterKeys = (spec: ColumnFilterSpec): string[] => {
  switch (spec.type) {
    case "text":
      return [`${spec.field}_q`];
    case "choice":
      return [spec.field];
    case "range":
      return [`${spec.field}@gte`, `${spec.field}@lte`];
    case "preset":
      return [...new Set(spec.options.flatMap((o) => Object.keys(o.filter)))];
  }
};

const sameValue = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

export const ColumnFilter = ({
  label,
  spec,
}: {
  label: string;
  spec: ColumnFilterSpec;
}) => {
  const { filterValues, setFilters } = useListContext();
  const [open, setOpen] = useState(false);
  const keys = columnFilterKeys(spec);
  const isActive = keys.some((key) => filterValues[key] !== undefined);

  const apply = (values: Record<string, unknown>) => {
    const rest = Object.fromEntries(
      Object.entries(filterValues).filter(([key]) => !keys.includes(key)),
    );
    const kept = Object.fromEntries(
      Object.entries(values).filter(
        ([, value]) =>
          value !== undefined &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0),
      ),
    );
    setFilters({ ...rest, ...kept });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "size-6",
            isActive ? "text-primary bg-accent" : "text-muted-foreground",
          )}
          aria-label={`Filter by ${label}`}
          aria-pressed={isActive}
        >
          <ListFilter className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 flex flex-col gap-3">
        <p className="text-sm font-medium">Filter by {label.toLowerCase()}</p>
        {spec.type === "text" ? (
          <TextFilter
            label={label}
            value={(filterValues[keys[0]] as string) ?? ""}
            onApply={(value) => apply({ [keys[0]]: value.trim() })}
          />
        ) : null}
        {spec.type === "choice" ? (
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
            {spec.options.map((option) => {
              const selected = (filterValues[spec.field] as string[]) ?? [];
              const checked = selected.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(on) =>
                      apply({
                        [spec.field]: on
                          ? [...selected, option.value]
                          : selected.filter((v) => v !== option.value),
                      })
                    }
                  />
                  {option.label}
                </label>
              );
            })}
            {spec.options.length === 0 ? (
              <p className="text-sm text-muted-foreground">No values yet.</p>
            ) : null}
          </div>
        ) : null}
        {spec.type === "range" ? (
          <RangeFilter
            min={filterValues[keys[0]] as number | undefined}
            max={filterValues[keys[1]] as number | undefined}
            onApply={(min, max) => apply({ [keys[0]]: min, [keys[1]]: max })}
          />
        ) : null}
        {spec.type === "preset" ? (
          <div className="flex flex-col gap-1">
            {spec.options.map((option) => {
              const selected = Object.entries(option.filter).every(
                ([key, value]) => sameValue(filterValues[key], value),
              );
              return (
                <Button
                  key={option.id}
                  variant={selected ? "secondary" : "ghost"}
                  size="sm"
                  className="justify-start"
                  onClick={() => {
                    apply(selected ? {} : option.filter);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
        ) : null}
        {isActive ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              apply({});
              setOpen(false);
            }}
          >
            Clear
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

const TextFilter = ({
  label,
  value,
  onApply,
}: {
  label: string;
  value: string;
  onApply: (value: string) => void;
}) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <form
      className="flex gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        onApply(text);
      }}
    >
      <Input
        autoFocus
        aria-label={`${label} contains`}
        placeholder="Contains…"
        value={text}
        onChange={(event) => setText(event.target.value)}
        className="h-8"
      />
      <Button type="submit" size="sm">
        Apply
      </Button>
    </form>
  );
};

const toNumber = (value: string) =>
  value.trim() === "" || Number.isNaN(Number(value))
    ? undefined
    : Number(value);

const RangeFilter = ({
  min,
  max,
  onApply,
}: {
  min?: number;
  max?: number;
  onApply: (min?: number, max?: number) => void;
}) => {
  const [from, setFrom] = useState(min?.toString() ?? "");
  const [to, setTo] = useState(max?.toString() ?? "");
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        onApply(toNumber(from), toNumber(to));
      }}
    >
      <div className="flex items-center gap-2">
        <Input
          aria-label="At least"
          placeholder="Min"
          inputMode="numeric"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          className="h-8"
        />
        <span className="text-muted-foreground">–</span>
        <Input
          aria-label="At most"
          placeholder="Max"
          inputMode="numeric"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          className="h-8"
        />
      </div>
      <Button type="submit" size="sm">
        Apply
      </Button>
    </form>
  );
};
